/**
 * Painel "Da venda à montagem": trilho das 8 etapas, etapa atual, módulos,
 * holofote que segue o mouse e partículas.
 */
import React, { useEffect, useRef } from "react";

import Poeira from "./Poeira";
import useCicloDasEtapas from "./useCicloDasEtapas";
import { ETAPAS, classeDoNo, numeroDaEtapa, preenchimentoDoTrilho } from "./etapas";
import { IconeCaixa, IconeDocumento, IconeFerramenta, IconeLinhas } from "./icones";

const HOLOFOTE_INICIAL = { x: 72, y: 28 };

const MODULOS = [
  { titulo: "Comercial", descricao: "Orçamentos por ambiente, parceiros e contratos.", Icone: IconeDocumento },
  { titulo: "PCP", descricao: "Programação e apontamentos por setor.", Icone: IconeLinhas },
  { titulo: "Compras e estoque", descricao: "Chapas, ferragens e itens de terceiros.", Icone: IconeCaixa },
  { titulo: "Montagem e assistência", descricao: "Obras, solicitações e autorizações.", Icone: IconeFerramenta },
];

function fundoDoHolofote(x, y) {
  return `radial-gradient(560px circle at ${x}% ${y}%, color-mix(in srgb, var(--a) 14%, transparent), transparent 65%)`;
}

/**
 * Holofote: escreve direto no estilo do elemento (um quadro por vez), sem
 * estado React — senão cada movimento do mouse re-renderizaria a cena.
 */
function useHolofote() {
  const ref = useRef(null);
  const raf = useRef(0);
  useEffect(() => () => window.cancelAnimationFrame(raf.current), []);
  const aoMover = (evento) => {
    const caixa = evento.currentTarget.getBoundingClientRect();
    const x = Math.round(((evento.clientX - caixa.left) / caixa.width) * 100);
    const y = Math.round(((evento.clientY - caixa.top) / caixa.height) * 100);
    if (raf.current) return;
    raf.current = window.requestAnimationFrame(() => {
      raf.current = 0;
      if (ref.current) ref.current.style.background = fundoDoHolofote(x, y);
    });
  };
  return { ref, aoMover };
}

function Trilho({ etapa, alvo, aoTerminarLinha }) {
  return (
    <div className="rail" role="list" aria-label="Etapas do pedido">
      <div className="rail-track" aria-hidden="true">
        <div
          className="rail-fill"
          style={{ width: `max(0px, calc(${preenchimentoDoTrilho(alvo)}% - var(--r)))` }}
          onTransitionEnd={aoTerminarLinha}
        />
      </div>
      {ETAPAS.map((item, indice) => (
        <div key={item.nome} className={classeDoNo(indice, etapa)} role="listitem">
          <span className="node-dot">{numeroDaEtapa(indice)}</span>
          <span className="node-l">{item.nome}</span>
        </div>
      ))}
    </div>
  );
}

function EtapaAtual({ etapa, fase }) {
  const item = ETAPAS[etapa];
  const classe = `stage${fase === "out" ? " out" : fase === "pre" ? " pre" : ""}`;
  // Sem aria-live de propósito: o texto troca a cada 3,2 s para sempre, e um
  // leitor de tela anunciaria cada troca por cima do formulário de login.
  return (
    <div className={classe}>
      <span className="stage-n">{numeroDaEtapa(etapa)}/08</span>
      <div>
        <div className="stage-t">{item.nome}</div>
        <div className="stage-d">{item.descricao}</div>
      </div>
    </div>
  );
}

function Modulos() {
  return (
    <div className="mods">
      {MODULOS.map(({ titulo, descricao, Icone }) => (
        <div key={titulo} className="mod">
          <div className="mod-i">
            <Icone />
          </div>
          <div className="mod-t">{titulo}</div>
          <div className="mod-d">{descricao}</div>
        </div>
      ))}
    </div>
  );
}

export default function CenaDoFluxo() {
  const { etapa, alvo, fase, aoTerminarLinha } = useCicloDasEtapas();
  const holofote = useHolofote();
  return (
    <section className="e-scene" aria-label="Como o Pharu ERP organiza a produção" onMouseMove={holofote.aoMover}>
      <div
        className="spot"
        aria-hidden="true"
        ref={holofote.ref}
        style={{ background: fundoDoHolofote(HOLOFOTE_INICIAL.x, HOLOFOTE_INICIAL.y) }}
      />
      <Poeira />
      <div className="kicker">Gestão de ponta a ponta</div>
      <h2 className="e-h">
        Da venda à montagem, <span>cada pedido num só fluxo.</span>
      </h2>
      <p className="e-p">
        O Pharu ERP conecta comercial, projetos, compras, fábrica e obra da sua marcenaria — com cada setor vendo
        exatamente o que precisa fazer agora.
      </p>
      <Trilho etapa={etapa} alvo={alvo} aoTerminarLinha={aoTerminarLinha} />
      <EtapaAtual etapa={etapa} fase={fase} />
      <Modulos />
    </section>
  );
}
